import { DOCUMENT, inject, Service } from '@angular/core';
import type { ParamMap } from '@angular/router';
import { Observable, switchMap } from 'rxjs';
import type { OnlinePaymentDetailsDto } from '@api/models';
import { OrderService } from '@/pages/checkout-page/order.service';

const WAYFORPAY_PAY_URL = 'https://secure.wayforpay.com/pay';

/** How WayForPay said the payment ended; the backend puts it in the address it sends the customer back to. */
export type WayforpayReturn
  = | { status: 'approved'; orderNumber: string; amount: number }
  | { status: 'pending' }
  | { status: 'declined'; message: string };

/**
 * Pays an order on the WayForPay page, for the amount the backend says is still due. Apple Pay does
 * not work inside an iframe, so the customer leaves the site and comes back through the return URL.
 */
@Service()
export class WayforpayService {
  private readonly document = inject(DOCUMENT);
  private readonly orderService = inject(OrderService);

  /**
   * Stays open while the browser leaves the page. Completes when the customer comes back with the
   * Back button to this page restored as it was. Errors when the payment details cannot be loaded.
   */
  pay(orderIdExpanded: number): Observable<never> {
    return this.orderService.fetchOnlinePaymentDetails(orderIdExpanded)
      .pipe(switchMap(details => this.submitToWayforpay(details)));
  }

  private submitToWayforpay(details: OnlinePaymentDetailsDto): Observable<never> {
    return new Observable<never>(subscriber => {
      const window = this.document.defaultView;
      const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          subscriber.complete();
        }
      };
      window?.addEventListener('pageshow', onPageShow);
      subscriber.add(() => window?.removeEventListener('pageshow', onPageShow));

      const form = this.buildForm(details);
      this.document.body.appendChild(form);
      form.submit();
      form.remove();
    });
  }

  private buildForm(details: OnlinePaymentDetailsDto): HTMLFormElement {
    const form = this.document.createElement('form');
    form.method = 'post';
    form.action = WAYFORPAY_PAY_URL;
    form.acceptCharset = 'utf-8';

    const addField = (name: string, value: unknown) => {
      const input = this.document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = `${value}`;
      form.appendChild(input);
    };

    for (const [name, value] of Object.entries(details)) {
      if (Array.isArray(value)) {
        value.forEach(item => addField(`${name}[]`, item));
      } else if (value !== null && value !== undefined) {
        addField(name, value);
      }
    }

    return form;
  }
}

/** A cancelled payment comes back with a reason but no status. */
const describeDecline = (params: ParamMap): string => {
  const status = params.get('transactionStatus');
  const reason = params.get('reason') || '- - -';
  const code = params.get('reasonCode') || '- - -';
  const details = `Причина: ${reason}. Код причини: ${code}`;
  return status ? `Статус: ${status}. ${details}` : details;
};

/** The result in the address the customer came back to; null on any other visit to the page. */
export const parseWayforpayReturn = (params: ParamMap): WayforpayReturn | null => {
  switch (params.get('paymentResult')) {
    case 'approved':
      return { status: 'approved', orderNumber: params.get('orderNumber') ?? '', amount: Number(params.get('amount')) };
    case 'pending':
      return { status: 'pending' };
    case 'declined':
      return { status: 'declined', message: describeDecline(params) };
    default:
      return null;
  }
};
